import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { TClusterStatus } from '~/types/cluster'

import Button from '~/components/Buttons/Button'
import CreatableSelectInput from '~/components/Inputs/ReactHookForm/CreatableSelectInput'
import MultiTextInput from '~/components/Inputs/ReactHookForm/MultiTextInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Label from '~/components/Label'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { CLUSTER_STATUSES } from '~/const/clusters'
import { useCreateClusterMutation, keys as clustersKeys } from '~/queries/clusters'
import { useUsersListQuery } from '~/queries/users'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { capitalize } from '~/utils/string'
import { displayToast } from '~/utils/toast'
import { RequiredYupOption } from '~/utils/yup'

import './styles.scss'

const MODAL_ID = 'create-cluster-modal'

interface FormValues {
  applications: IOption[]
  description?: string
  enableKeywordScrapper: boolean
  enableReviewsScrapper: boolean
  features?: string[]
  highVolumeThreshold: number
  name: string
  nbLeaders: number
  status: IOption<TClusterStatus>
  tags: IOption[]
  userId: IOption | null
}

const statusOptions = CLUSTER_STATUSES.map(({ label, value }) => ({ label, value }))

const validationSchema = Yup.object().shape({
  applications: Yup.array().of(RequiredYupOption).required(),
  description: Yup.string(),
  enableKeywordScrapper: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  enableReviewsScrapper: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  features: Yup.array(),
  highVolumeThreshold: Yup.number()
    .typeError(FORM_ERRORS.NUMBER)
    .required(FORM_ERRORS.REQUIRED)
    .min(0, FORM_ERRORS.MIN_VALUE(0)),
  name: Yup.string().required(FORM_ERRORS.REQUIRED),
  nbLeaders: Yup.number().typeError(FORM_ERRORS.NUMBER).required(FORM_ERRORS.REQUIRED).min(0, FORM_ERRORS.MIN_VALUE(0)),
  status: Yup.object()
    .shape({ label: Yup.string().required(), value: Yup.mixed<TClusterStatus>().required() })
    .required(FORM_ERRORS.REQUIRED),
  tags: Yup.array().of(RequiredYupOption).required(),
  userId: RequiredYupOption.nullable(),
})

interface ModalFormProps {
  clusterId?: string
  clusterTags: string[]
  defaultApps?: string[]
  onClose: VoidFunction
  onSuccess: VoidFunction
}

const CreateClusterForm = ({
  clusterId,
  clusterTags,
  defaultApps,
  onClose,
  onSuccess,
}: ModalFormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues: {
      applications: defaultApps?.map((app) => ({ label: app, value: app })) || [],
      description: '',
      enableKeywordScrapper: true,
      enableReviewsScrapper: true,
      features: [],
      highVolumeThreshold: 0,
      name: '',
      nbLeaders: 0,
      status: statusOptions[0],
      tags: [],
      userId: null,
    },
    resolver: yupResolver(validationSchema),
  })

  const options = clusterTags
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((el) => ({ label: el, value: el }))

  /** Queries & Mutations */

  const { mutateAsync: createClusterMutation } = useCreateClusterMutation()

  const { data: users } = useUsersListQuery()

  /** Handlers */

  const userOptions =
    users
      ?.filter(({ name }) => name !== '')
      .map(({ name, user_id }) => ({ label: capitalize(name), value: user_id }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []

  const handleFormSubmit = async (values: FormValues) => {
    const noEmptyStringFeatures = values.features?.filter((el) => el !== '')

    try {
      await createClusterMutation({
        description: values.description || '',
        features: noEmptyStringFeatures || [],
        highVolumeThreshold: values.highVolumeThreshold,
        isGetKeywordsEnabled: values.enableKeywordScrapper,
        isGetReviewsEnabled: values.enableReviewsScrapper,
        name: values.name,
        nbLeaders: values.nbLeaders,
        status: values.status.value,
        storeIds: values.applications.map(({ value }) => value),
        tags: values.tags.map(({ value }) => value),
        userId: values.userId?.value || '',
      })
      if (clusterId) await queryClient.invalidateQueries({ queryKey: clustersKeys.details(clusterId) })
      await queryClient.invalidateQueries({ queryKey: clustersKeys.listClusters() })
      onSuccess()
    } catch (e) {
      captureException(e)
      displayToast('Cluster could not be created', 'error')
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <fieldset className="create-cluster-form__fieldset">
          <TextInput
            {...register('name')}
            error={errors.name?.message}
            label="Cluster name"
            placeholder="Enter a cluster name"
            required
          />
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.status?.message}
                label="Status"
                options={statusOptions}
                required
              />
            )}
          />
          <Controller
            control={control}
            name="applications"
            render={(field) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                isMulti
                label="App(s)"
                placeholder='Paste a store ID and press "Enter"'
              />
            )}
          />
          <TextArea
            {...register('description')}
            error={errors.description?.message}
            label="Description"
            placeholder="Enter a description"
          />
          <Controller
            control={control}
            name="userId"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.userId?.message}
                label="User assigned"
                options={userOptions}
              />
            )}
          />
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                className="edit-cluster-tags-form__tags-input"
                isMulti
                label="Tags"
                openMenuOnClick={clusterTags.length > 0}
                options={options}
                placeholder='Type a tag and press "Enter"'
              />
            )}
          />
          <Controller
            control={control}
            name="features"
            render={({ field }) => (
              <MultiTextInput
                {...field}
                className="infos-form__main__input__multi-text"
                error={errors.features?.message}
                label="Features"
                placeholder="Enter feature"
              />
            )}
          />
          <TextInput
            error={errors.highVolumeThreshold?.message}
            label="High Volume Threshold"
            min={0}
            placeholder="Enter high volume threshold"
            required
            type="number"
            {...register('highVolumeThreshold')}
          />
        </fieldset>
        <hr className="create-cluster-form__separator" />
        <fieldset className="create-cluster-form__fieldset">
          <span className="create-cluster-form__fieldset__legend">Advanced settings</span>
          <TextInput
            {...register('nbLeaders')}
            error={errors.nbLeaders?.message}
            label="Nb leaders"
            min={0}
            placeholder="Enter number of leaders"
            required
            type="number"
          />
          <div className="rhf-checkbox-input-wrapper">
            <Label id="enableKeywordScrapper" label="Enable keyword scrapper" />
            <input {...register('enableKeywordScrapper')} id="enableKeywordScrapper" type="checkbox" />
          </div>
          <div className="rhf-checkbox-input-wrapper">
            <Label id="enableReviewsScrapper" label="Enable reviews scrapper" />
            <input {...register('enableReviewsScrapper')} id="enableReviewsScrapper" type="checkbox" />
          </div>
        </fieldset>
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Create
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends ModalFormProps {
  isOpen: boolean
}

const CreateClusterModal = ({ isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal className="create-cluster-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Create cluster</ModalHeader>
      <CreateClusterForm {...props} onClose={onClose} />
    </Modal>
  )
}

export default CreateClusterModal
